import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, requireAuthToken } from '@/lib/api'
import { requestDocumentFile } from './DocumentDownload'
import type { CollectedDocument, SigningState } from './model'

export interface IssuedDocument {
  id: string
  form_key: string
  configuration_id: string | null
  configuration_name: string
  member_id: string | null
  member_name: string
  state: SigningState
  created_at: string
  signed_at: string | null
  activity: {
    id: string
    label: string
    at: string
    call_id?: string
    reason?: string
    actor?: string
  }[]
}

export function useSigningDocumentsQuery(clientId?: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  return useQuery({
    queryKey: ['issued-signing-documents', organization?.id, clientId],
    enabled: Boolean(organization?.id),
    queryFn: async ({ signal }): Promise<CollectedDocument[]> => {
      const token = await requireAuthToken(getToken)
      const documents = new Map<string, CollectedDocument>()
      let offset: number | null = 0
      // Finish the paged read before applying the existing client-side filters
      // and sorting, so a match on a later page cannot disappear from Search.
      while (offset !== null) {
        const page: {
          documents: IssuedDocument[]
          next_offset: number | null
        } = await fetchJsonWithAuth({
          token,
          signal,
          url: `${AGENT_URL}/signing/documents?limit=100&offset=${offset}${clientId ? `&client_id=${encodeURIComponent(clientId)}` : ''}`,
          errorMessage: 'Could not load documents.',
        })
        for (const row of page.documents)
          documents.set(row.id, {
            id: row.id,
            formKey: row.form_key,
            formName: row.form_key.toUpperCase(),
            configurationId: row.configuration_id ?? undefined,
            configurationName: row.configuration_name,
            memberId: row.member_id ?? undefined,
            memberName: row.member_name,
            state: row.state,
            createdAt: row.created_at,
            signedAt: row.signed_at ?? undefined,
            activity: row.activity.map((event) => ({
              ...event,
              callId: event.call_id,
            })),
          })
        if (page.next_offset !== null && page.next_offset <= offset)
          throw new Error('Could not load the complete document list.')
        offset = page.next_offset
      }
      return [...documents.values()]
    },
    gcTime: 0,
    staleTime: 30_000,
    retry: false,
  })
}

export function useSigningDocumentPdf(documentId: string, version = '') {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  return useQuery({
    queryKey: ['signing-document-pdf', organization?.id, documentId, version],
    enabled: Boolean(organization?.id),
    queryFn: async ({ signal }) => {
      const file = await requestDocumentFile(
        await requireAuthToken(getToken),
        documentId
      )
      // Fetch the preserved PDF once. Blob viewing avoids expiring range reads
      // while someone spends time reviewing a document in the sheet.
      const response = await fetch(file.url, { signal, credentials: 'omit' })
      if (!response.ok) throw new Error('Could not load document.')
      const blob = await response.blob()
      if (!(await blob.slice(0, 5).text()).startsWith('%PDF-'))
        throw new Error('Document is not a PDF.')
      return new Blob([blob], { type: 'application/pdf' })
    },
    gcTime: 0,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
